import { Box, CircularProgress } from "@mui/material";
import { useMemo } from "react";

import {
  useResolvedRateSchedules,
  useUpdateRate,
  useUpdateProposalRate,
} from "../../../hooks/useRates";
import { formatRateValue } from "../../../utils/rateFormatters";
import { rateFieldPathBuilder } from "../../../utils/rateFieldPath";
import {
  getRenewalColumns,
  primarySectionSchedules,
  slotKeyOf,
  type RenewalColumns,
} from "../../../utils/rateSets";

import { EnrollmentFlag } from "./EnrollmentFlag";
import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  RenewalGroupHeaderCell,
  RenewalHeaderCell,
  RenewalRateCell,
  RenewalSubHeaderCells,
} from "./RenewalRateCell";
import {
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  FONT_SIZE,
  getQuantityLabel,
  getUnitLabel,
  gridCellSx,
  gridRowSx,
  headerCellSx,
  inputSx,
} from "./constants";

import type { ColDef } from "ag-grid-community";
import type { AxiosInstance } from "axios";
import type { FC, JSX } from "react";
import type { Rate } from "../../../types";
import type { EnrollmentMeasure } from "../../../utils/quotedEnrollmentReview";
import type {
  RateCellField,
  RateFieldPathBuilder,
} from "../../../utils/rateFieldPath";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";

export interface FlatRateRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type (e.g., dental, vision, std) for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
}

/**
 * Build column definitions for flat rate design in RatesGrid
 * Flat rate has a single rate value, no additional columns needed
 */
export const buildFlatRateColumns = (_group: BenefitPlanGroup): ColDef[] =>
  // Flat rate doesn't need additional sub-columns within the group column
  // All data is shown in a single cell
  [];

/**
 * Build column definitions for flat rate design in SummaryGrid
 * Returns array of ColDef for sub-columns within a benefit plan
 */
export const buildFlatRateSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] =>
  // TODO: Implement flat rate summary column generation
  // For flat rate, typically just one column showing the total cost
  [];

/**
 * Determine the structure detail from rates array
 */
type StructureDetail = "none" | "gender" | "tobacco";

const getStructureDetail = (rates: Rate[]): StructureDetail => {
  if (rates.length === 0) return "none";

  // Check if we have gender-differentiated rates
  const hasGender = rates.some((r) => r.gender != null);
  if (hasGender) return "gender";

  // Only a rate actually flagged as tobacco makes this a tobacco split. Legacy
  // payloads set isTobaccoRated=false on every rate of a schedule that isn't
  // tobacco-rated at all, which is not a split — rendering one would leave an
  // empty Tobacco column beside the real rate.
  const hasTobacco = rates.some((r) => r.isTobaccoRated === true);
  if (hasTobacco) return "tobacco";

  return "none";
};

/** Parameters for rate update mutations */
interface RateUpdateParams {
  scheduleId: string;
  rateId: string;
  benefitPlanId: string;
  rate: { rate?: number; volume?: number; numberOfLives?: number };
}

/** Context for creating rate handlers */
interface RateHandlerContext {
  axiosInstance: AxiosInstance | undefined;
  scheduleId: string | undefined;
  planId: string;
  mutate: (params: RateUpdateParams) => void;
}

/** PEPM schedules and flat "usd"-unit rates (unit-based, not volume-based) both
 * store their quantity in numberOfLives rather than volume, and should never
 * render a currency-formatted quantity input. */
const usesNumberOfLives = (rate: Rate | undefined, isPepm: boolean): boolean =>
  isPepm || rate?.unit === "usd";

/** Create a handler for updating quantity (volume or numberOfLives) */
const createQuantityHandler =
  (
    ctx: RateHandlerContext,
    rate: Rate | undefined,
    isPepm: boolean,
  ): ((value: string) => void) =>
  (value: string): void => {
    if (!ctx.axiosInstance || !ctx.scheduleId || !rate?.id) return;

    if (usesNumberOfLives(rate, isPepm)) {
      const numericValue = parseInt(value, 10);
      if (isNaN(numericValue) || numericValue === rate.numberOfLives) return;
      ctx.mutate({
        scheduleId: ctx.scheduleId,
        rateId: rate.id,
        benefitPlanId: ctx.planId,
        rate: { numberOfLives: numericValue },
      });
    } else {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue) || numericValue === rate.volume) return;
      ctx.mutate({
        scheduleId: ctx.scheduleId,
        rateId: rate.id,
        benefitPlanId: ctx.planId,
        rate: { volume: numericValue },
      });
    }
  };

const IDI_COVERAGE_TYPE = "individualdisabilityinsurance";
const ENROLLED_LIVES_LABEL = "Enrolled Lives";

/**
 * The IDI marketing presentation shows Enrolled Lives beside the Covered
 * Benefit a volume-based rate is priced on, so an IDI flat rate collects the
 * count in numberOfLives, which a volume-based rate leaves unused.
 */
const collectsEnrolledLives = (
  rate: Rate | undefined,
  isPepm: boolean,
  coverageType?: string,
): boolean =>
  coverageType?.toLowerCase() === IDI_COVERAGE_TYPE &&
  !usesNumberOfLives(rate, isPepm);

// The BTX rate repositories clear a field when it receives -1.
const CLEAR_RATE_FIELD = -1;

/**
 * Create a handler for updating a volume-based rate's enrolled lives. An
 * emptied input clears the saved count.
 */
const createEnrolledLivesHandler =
  (
    ctx: RateHandlerContext,
    rate: Rate | undefined,
  ): ((value: string) => void) =>
  (value: string): void => {
    if (!ctx.axiosInstance || !ctx.scheduleId || !rate?.id) return;
    const isCleared = value.trim() === "";
    if (isCleared && rate.numberOfLives == null) return;
    const numericValue = isCleared ? CLEAR_RATE_FIELD : parseInt(value, 10);
    if (isNaN(numericValue) || numericValue === rate.numberOfLives) return;
    ctx.mutate({
      scheduleId: ctx.scheduleId,
      rateId: rate.id,
      benefitPlanId: ctx.planId,
      rate: { numberOfLives: numericValue },
    });
  };

/** Create a handler for updating rate value */
const createRateHandler =
  (
    ctx: RateHandlerContext,
    rate: Rate | undefined,
  ): ((value: string) => void) =>
  (value: string): void => {
    if (!ctx.axiosInstance || !ctx.scheduleId || !rate?.id) return;
    const numericValue = parseFloat(value);
    if (isNaN(numericValue) || numericValue === rate.rate) return;
    ctx.mutate({
      scheduleId: ctx.scheduleId,
      rateId: rate.id,
      benefitPlanId: ctx.planId,
      rate: { rate: numericValue },
    });
  };

// Local grid layouts
// CSS Grid container styling - 2 columns: Label (flex), Value (flex). With renewal a third
// column is appended and the table scrolls within the pinned benefit-plan cell.
const singleGridSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(90px, 1fr) repeat(2, minmax(80px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : { display: "grid", gridTemplateColumns: "3fr 2fr", width: "100%" };

// Dual column grid container styling - Label + primary + secondary, plus a renewal column
// per value column when renewal is shown.
const dualGridSx = (showRenewal: boolean): Record<string, unknown> => ({
  display: "grid",
  gridTemplateColumns: `minmax(80px, 1fr) repeat(${
    showRenewal ? 4 : 2
  }, minmax(80px, 1fr))`,
  width: "100%",
  ...(showRenewal ? { overflowX: "auto" } : {}),
});

/** Props for extracted layout components */
interface LayoutProps {
  planId: string;
  rateTypeLabel: string;
  quantityLabel: string;
  isPepm: boolean;
  primaryRate: Rate | undefined;
  secondaryRate?: Rate | undefined;
  primaryLabel?: string;
  secondaryLabel?: string;
  onPrimaryQuantityCommit: (value: string) => void;
  onPrimaryRateCommit: (value: string) => void;
  onSecondaryQuantityCommit?: (value: string) => void;
  onSecondaryRateCommit?: (value: string) => void;
  /** Set only where the rate collects enrolled lives; see collectsEnrolledLives. */
  onEnrolledLivesCommit?: (value: string) => void;
  readOnly?: boolean;
  renewal: RenewalColumns;
  fieldPathOf: RateFieldPathBuilder;
  coverageType?: string;
}

/** Which stored field a quantity cell edits, matching `getQuantityValue`. */
const quantityField = (
  rate: Rate | undefined,
  isPepm: boolean,
): RateCellField =>
  usesNumberOfLives(rate, isPepm) ? "numberOfLives" : "volume";

/** What a flat rate's quantity cell shows: lives for a per-unit rate, volume otherwise. */
const enrollmentMeasureOf = (
  rate: Rate | undefined,
  isPepm: boolean,
): EnrollmentMeasure => (usesNumberOfLives(rate, isPepm) ? "lives" : "volume");

/** Get display value for quantity field */
const getQuantityValue = (rate: Rate | undefined, isPepm: boolean): string => {
  if (usesNumberOfLives(rate, isPepm)) {
    return rate?.numberOfLives != null ? String(rate.numberOfLives) : "";
  }
  return rate?.volume != null ? String(rate.volume) : "";
};

/** Single column layout component */
const FlatRateSingleColumn: FC<LayoutProps> = ({
  planId,
  rateTypeLabel,
  quantityLabel,
  isPepm,
  primaryRate,
  onPrimaryQuantityCommit,
  onPrimaryRateCommit,
  onEnrolledLivesCommit,
  readOnly,
  renewal,
  fieldPathOf,
  coverageType,
}): JSX.Element => {
  const showRenewal = renewal.showRenewal;
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Rate Design Label Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>{rateTypeLabel}</Box>
      </Box>

      <Box sx={singleGridSx(showRenewal)}>
        {/* Column header row — only needed to name Current vs Renewal */}
        {showRenewal && (
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box sx={{ ...headerCellSx, justifyContent: "flex-end" }}>
              Current
            </Box>
            <RenewalHeaderCell show={showRenewal} label={renewal.columnLabel} />
          </Box>
        )}

        {onEnrolledLivesCommit && (
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }}>{ENROLLED_LIVES_LABEL}</Box>
            <RateReviewCell
              planId={planId}
              fieldPath={fieldPathOf(primaryRate, "numberOfLives")}
              sx={{
                ...gridCellSx,
                borderRight: showRenewal ? AG_BORDER : "none",
              }}
            >
              <EnrollmentFlag rate={primaryRate} measure="lives">
                <DebouncedNumericInput
                  serverValue={
                    primaryRate?.numberOfLives != null
                      ? String(primaryRate.numberOfLives)
                      : ""
                  }
                  onCommit={onEnrolledLivesCommit}
                  allowDecimals={false}
                  sx={inputSx}
                  gridId={`flat-rate-${planId}`}
                  cellIndex={0}
                  columnsInRow={1}
                  readOnly={readOnly}
                  inputProps={{ "aria-label": ENROLLED_LIVES_LABEL }}
                />
              </EnrollmentFlag>
            </RateReviewCell>
            {showRenewal && <Box sx={{ borderLeft: AG_BORDER }} />}
          </Box>
        )}

        {/* Quantity Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>{quantityLabel}</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(
              primaryRate,
              quantityField(primaryRate, isPepm),
            )}
            sx={{
              ...gridCellSx,
              borderRight: showRenewal ? AG_BORDER : "none",
            }}
          >
            <EnrollmentFlag
              rate={primaryRate}
              measure={enrollmentMeasureOf(primaryRate, isPepm)}
            >
              <DebouncedNumericInput
                serverValue={getQuantityValue(primaryRate, isPepm)}
                onCommit={onPrimaryQuantityCommit}
                isCurrency={!usesNumberOfLives(primaryRate, isPepm)}
                sx={inputSx}
                gridId={`flat-rate-${planId}`}
                cellIndex={0}
                columnsInRow={1}
                readOnly={readOnly}
                inputProps={{ "aria-label": quantityLabel }}
              />
            </EnrollmentFlag>
          </RateReviewCell>
          {showRenewal && <Box sx={{ borderLeft: AG_BORDER }} />}
        </Box>

        {/* Rate Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx, borderBottom: "none" }}>
            {isPepm ? "PEPM" : getUnitLabel(primaryRate?.unit)}
          </Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(primaryRate, "rate")}
            sx={{
              ...gridCellSx,
              borderRight: showRenewal ? AG_BORDER : "none",
              borderBottom: "none",
            }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                primaryRate?.rate,
                primaryRate?.unit,
                coverageType,
              )}
              onCommit={onPrimaryRateCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`flat-rate-${planId}`}
              cellIndex={0}
              columnsInRow={1}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Rate" }}
            />
          </RateReviewCell>
          {showRenewal && primaryRate && (
            <RenewalRateCell
              slotKey={slotKeyOf(primaryRate)}
              renewal={renewal}
              rowLabel="Rate"
              isLastRow
              coverageType={coverageType}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

/** Dual column layout component (gender or tobacco split) */
const FlatRateDualColumn: FC<LayoutProps> = ({
  planId,
  rateTypeLabel,
  quantityLabel,
  isPepm,
  primaryRate,
  secondaryRate,
  primaryLabel = "",
  secondaryLabel = "",
  onPrimaryQuantityCommit,
  onPrimaryRateCommit,
  onSecondaryQuantityCommit,
  onSecondaryRateCommit,
  readOnly,
  renewal,
  fieldPathOf,
  coverageType,
}): JSX.Element => {
  const showRenewal = renewal.showRenewal;
  const currentEndBorderRight = showRenewal ? AG_BORDER : "none";
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Rate Design Label Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>{rateTypeLabel}</Box>
      </Box>

      <Box sx={dualGridSx(showRenewal)}>
        {/* Group header row — only when renewal columns are present */}
        {showRenewal && (
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box
              sx={{
                ...headerCellSx,
                gridColumn: "span 2",
                justifyContent: "center",
                fontWeight: 600,
                borderRight: AG_BORDER,
              }}
            >
              Current
            </Box>
            <RenewalGroupHeaderCell
              show={showRenewal}
              label={renewal.columnLabel}
              span={2}
            />
          </Box>
        )}

        {/* Column label row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }} />
          <Box
            sx={{ ...headerCellSx, justifyContent: "center", fontWeight: 600 }}
          >
            {primaryLabel}
          </Box>
          <Box
            sx={{
              ...headerCellSx,
              justifyContent: "center",
              fontWeight: 600,
              borderRight: currentEndBorderRight,
            }}
          >
            {secondaryLabel}
          </Box>
          <RenewalSubHeaderCells
            show={showRenewal}
            labels={[primaryLabel, secondaryLabel]}
          />
        </Box>

        {/* Quantity Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>{quantityLabel}</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(
              primaryRate,
              quantityField(primaryRate, isPepm),
            )}
            sx={{ ...gridCellSx }}
          >
            <EnrollmentFlag
              rate={primaryRate}
              measure={enrollmentMeasureOf(primaryRate, isPepm)}
            >
              <DebouncedNumericInput
                serverValue={getQuantityValue(primaryRate, isPepm)}
                onCommit={onPrimaryQuantityCommit}
                isCurrency={!usesNumberOfLives(primaryRate, isPepm)}
                sx={inputSx}
                gridId={`flat-rate-${planId}`}
                cellIndex={0}
                columnsInRow={2}
                readOnly={readOnly}
                inputProps={{
                  "aria-label": `${primaryLabel} ${quantityLabel}`,
                }}
              />
            </EnrollmentFlag>
          </RateReviewCell>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(
              secondaryRate,
              quantityField(secondaryRate, isPepm),
            )}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <EnrollmentFlag
              rate={secondaryRate}
              measure={enrollmentMeasureOf(secondaryRate, isPepm)}
            >
              <DebouncedNumericInput
                serverValue={getQuantityValue(secondaryRate, isPepm)}
                onCommit={onSecondaryQuantityCommit ?? ((): void => {})}
                isCurrency={!usesNumberOfLives(secondaryRate, isPepm)}
                sx={inputSx}
                gridId={`flat-rate-${planId}`}
                cellIndex={1}
                columnsInRow={2}
                readOnly={readOnly}
                inputProps={{
                  "aria-label": `${secondaryLabel} ${quantityLabel}`,
                }}
              />
            </EnrollmentFlag>
          </RateReviewCell>
          {showRenewal && (
            <>
              <Box sx={{ borderLeft: AG_BORDER }} />
              <Box sx={{ borderLeft: AG_BORDER }} />
            </>
          )}
        </Box>

        {/* Rate Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx, borderBottom: "none" }}>
            {isPepm ? "PEPM" : getUnitLabel(primaryRate?.unit)}
          </Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(primaryRate, "rate")}
            sx={{ ...gridCellSx, borderBottom: "none" }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                primaryRate?.rate,
                primaryRate?.unit,
                coverageType,
              )}
              onCommit={onPrimaryRateCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`flat-rate-${planId}`}
              cellIndex={2}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": `${primaryLabel} Rate` }}
            />
          </RateReviewCell>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(secondaryRate, "rate")}
            sx={{
              ...gridCellSx,
              borderRight: currentEndBorderRight,
              borderBottom: "none",
            }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                secondaryRate?.rate,
                secondaryRate?.unit,
                coverageType,
              )}
              onCommit={onSecondaryRateCommit ?? ((): void => {})}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`flat-rate-${planId}`}
              cellIndex={3}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": `${secondaryLabel} Rate` }}
            />
          </RateReviewCell>
          {showRenewal && primaryRate && (
            <RenewalRateCell
              slotKey={slotKeyOf(primaryRate)}
              renewal={renewal}
              rowLabel={primaryLabel}
              isLastRow
              coverageType={coverageType}
            />
          )}
          {showRenewal && secondaryRate && (
            <RenewalRateCell
              slotKey={slotKeyOf(secondaryRate)}
              renewal={renewal}
              rowLabel={secondaryLabel}
              isLastRow
              coverageType={coverageType}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Renderer for flat rate design using styled divs and inputs
 * Displays:
 * - Single column when no structure detail (None)
 * - Two side-by-side columns when Gender (Male/Female) or Tobacco (Tobacco/Non-Tobacco)
 *
 * For PEPM rates (detected by rateSchedule.pepm being set):
 * - Uses numberOfLives field instead of volume
 * - Shows "Number of Enrolled" label instead of "Covered Benefit"
 *
 * For an IDI volume-based rate in the single-column layout, adds an
 * "Enrolled Lives" row above the quantity, stored in numberOfLives.
 *
 * Updates rate on blur via PATCH API
 */
export const FlatRateRenderer: FC<FlatRateRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
}) => {
  const { data: rateSchedules, isLoading } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );

  const rateSchedule = rateSchedules?.[0];
  const fieldPathOf = rateFieldPathBuilder(rateSchedule);

  // Renewal display is derived from all categories of this section's (non-AD&D) schedules;
  // AD&D rates are scoped out so they can't collide onto a primary slot. Carrier plans and
  // incumbents with none entered resolve to showRenewal === false.
  const renewal = useMemo(
    () => getRenewalColumns(primarySectionSchedules(rateSchedules)),
    [rateSchedules],
  );

  // Memoize rates to prevent dependency issues in downstream useMemo hooks
  // Filter out dependent rates (those with coverageTierCode) — they are rendered
  // by DependentRateRenderer in a separate section below
  const rates = useMemo(
    () =>
      (rateSchedule?.rates || []).filter(
        (r) =>
          !r.coverageTierCode ||
          !["employeeDependent", "spouseOnly", "childOnly"].includes(
            r.coverageTierCode,
          ),
      ),
    [rateSchedule?.rates],
  );

  // Detect if this is a PEPM rate schedule
  const isPepm = rateSchedule?.pepm != null;

  // Determine structure detail from rates
  const structureDetail = useMemo(() => getStructureDetail(rates), [rates]);

  // For gender: find male and female rates
  // For tobacco: find tobacco and non-tobacco rates
  // For none: just use first rate
  const { primaryRate, secondaryRate, primaryLabel, secondaryLabel } =
    useMemo(() => {
      if (structureDetail === "gender") {
        const maleRate = rates.find((r) => r.gender === "male");
        const femaleRate = rates.find((r) => r.gender === "female");
        return {
          primaryRate: maleRate,
          secondaryRate: femaleRate,
          primaryLabel: "Male",
          secondaryLabel: "Female",
        };
      }
      if (structureDetail === "tobacco") {
        const tobaccoRate = rates.find((r) => r.isTobaccoRated === true);
        const nonTobaccoRate = rates.find((r) => r.isTobaccoRated === false);
        return {
          primaryRate: tobaccoRate,
          secondaryRate: nonTobaccoRate,
          primaryLabel: "Tobacco",
          secondaryLabel: "Non-Tobacco",
        };
      }
      return {
        primaryRate: rates[0],
        secondaryRate: undefined,
        primaryLabel: "",
        secondaryLabel: "",
      };
    }, [rates, structureDetail]);

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  const mutateRate = useMemo(
    () =>
      (params: RateUpdateParams): void => {
        rateMutation.mutate(params, {
          onSuccess: () => onRateSaved(),
        });
      },
    [onRateSaved, rateMutation],
  );

  // Create handler context
  const handlerCtx = useMemo(
    (): RateHandlerContext => ({
      axiosInstance,
      scheduleId: rateSchedule?.id,
      planId: plan.id,
      mutate: mutateRate,
    }),
    [axiosInstance, rateSchedule?.id, plan.id, mutateRate],
  );

  // Create handlers using factory functions
  const handlePrimaryQuantityCommit = useMemo(
    () => createQuantityHandler(handlerCtx, primaryRate, isPepm),
    [handlerCtx, primaryRate, isPepm],
  );

  const handlePrimaryRateCommit = useMemo(
    () => createRateHandler(handlerCtx, primaryRate),
    [handlerCtx, primaryRate],
  );

  const handleEnrolledLivesCommit = useMemo(
    () => createEnrolledLivesHandler(handlerCtx, primaryRate),
    [handlerCtx, primaryRate],
  );

  const handleSecondaryQuantityCommit = useMemo(
    () => createQuantityHandler(handlerCtx, secondaryRate, isPepm),
    [handlerCtx, secondaryRate, isPepm],
  );

  const handleSecondaryRateCommit = useMemo(
    () => createRateHandler(handlerCtx, secondaryRate),
    [handlerCtx, secondaryRate],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  const hasSplit =
    structureDetail === "gender" || structureDetail === "tobacco";

  // Labels based on rate type
  const rateTypeLabel = isPepm ? "PEPM" : "Flat";
  const quantityLabel = getQuantityLabel(
    primaryRate?.unit,
    isPepm,
    coverageType,
  );

  // Single column layout for no structure detail
  if (!hasSplit) {
    return (
      <FlatRateSingleColumn
        planId={plan.id}
        rateTypeLabel={rateTypeLabel}
        quantityLabel={quantityLabel}
        isPepm={isPepm}
        primaryRate={primaryRate}
        onPrimaryQuantityCommit={handlePrimaryQuantityCommit}
        onPrimaryRateCommit={handlePrimaryRateCommit}
        onEnrolledLivesCommit={
          collectsEnrolledLives(primaryRate, isPepm, coverageType)
            ? handleEnrolledLivesCommit
            : undefined
        }
        readOnly={readOnly}
        renewal={renewal}
        fieldPathOf={fieldPathOf}
        coverageType={coverageType}
      />
    );
  }

  // Dual column layout for gender or tobacco
  return (
    <FlatRateDualColumn
      planId={plan.id}
      rateTypeLabel={rateTypeLabel}
      quantityLabel={quantityLabel}
      isPepm={isPepm}
      primaryRate={primaryRate}
      secondaryRate={secondaryRate}
      primaryLabel={primaryLabel}
      secondaryLabel={secondaryLabel}
      onPrimaryQuantityCommit={handlePrimaryQuantityCommit}
      onPrimaryRateCommit={handlePrimaryRateCommit}
      onSecondaryQuantityCommit={handleSecondaryQuantityCommit}
      onSecondaryRateCommit={handleSecondaryRateCommit}
      readOnly={readOnly}
      renewal={renewal}
      fieldPathOf={fieldPathOf}
      coverageType={coverageType}
    />
  );
};
